import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/layout/ScrollToTop';
import SeoHead from './components/common/SeoHead';

// Critical components loaded immediately for better performance
import Home from './components/Home';
import PrivateRoute from './components/auth/PrivateRoute';
import Login from './components/auth/Login';
import ContactPage from './components/ContactPage';
import LoadSheddingPage from './components/loadShedding/LoadSheddingPage';
import NewsPage from './components/NewsPage';
import InsightPage from './components/InsightPage';
import ProductPage from './components/ProductPage';

// Lazy-loaded components for code splitting
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const ProductList = React.lazy(() => import('./components/products/ProductList'));
const ProductDetail = React.lazy(() => import('./components/products/ProductDetail'));
const SavingsCalculator = React.lazy(() => import('./components/calculator/SavingsCalculator'));
const PanelSizeCalculator = React.lazy(() => import('./components/calculator/PanelSizeCalculator'));
const BatteryCapacityCalculator = React.lazy(() => import('./components/calculator/BatteryCapacityCalculator'));
const CleaningCalculator = React.lazy(() => import('./components/calculator/CleaningCalculator'));
const SolarGeyserCalculator = React.lazy(() => import('./components/calculator/SolarGeyserCalculator'));
const SolarFinancingCalculator = React.lazy(() => import('./components/calculator/SolarFinancingCalculator'));
const ReviewsPage = React.lazy(() => import('./components/ReviewsPage'));
const SolarPanelPage = React.lazy(() => import('./components/products/SolarPanelPage'));
const BatteryPage = React.lazy(() => import('./components/products/BatteryPage'));
const InverterPage = React.lazy(() => import('./components/products/InverterPage'));
const MountingSystemPage = React.lazy(() => import('./components/products/MountingSystemPage'));
const ChargeControllerPage = React.lazy(() => import('./components/products/ChargeControllerPage'));
const ThankYouPage = React.lazy(() => import('./components/ThankYouPage'));
const SearchResults = React.lazy(() => import('./components/SearchResults'));
const SolarTiltGauge = React.lazy(() => import('./components/calculators/SolarTiltGauge'));
const SignIn = React.lazy(() => import('./components/auth/SignIn'));
const SignUp = React.lazy(() => import('./components/auth/SignUp'));

// Service Pages - lazy loaded
const SolarServicePage = React.lazy(() => import('./components/services/SolarServicePage'));
const SolarCleaningPage = React.lazy(() => import('./components/services/SolarCleaningPage'));
const SolarRepairPage = React.lazy(() => import('./components/services/SolarRepairPage'));
const SolarMaintenancePage = React.lazy(() => import('./components/services/SolarMaintenancePage'));
const SolarInstallationPage = React.lazy(() => import('./components/services/SolarInstallationPage'));

// Social Tips - lazy loaded
const SocialTipsList = React.lazy(() => import('./components/social-tips/SocialTipsList'));
const SocialTipDetail = React.lazy(() => import('./components/social-tips/SocialTipDetail'));
const AddTip = React.lazy(() => import('./components/social-tips/AddTip'));
const ManageTips = React.lazy(() => import('./components/social-tips/ManageTips'));

// Articles - lazy loaded
const PerovskiteSolarBreakthrough = React.lazy(() => import('./components/articles/PerovskiteSolarBreakthrough'));
const LoadSheddingSouthAfrica2025 = React.lazy(() => import('./components/articles/LoadSheddingSouthAfrica2025'));
const SolarRegistrationLaws2025 = React.lazy(() => import('./components/articles/SolarRegistrationLaws2025'));
const Stage6LoadsheddingSA = React.lazy(() => import('./components/articles/Stage6LoadsheddingSA'));
const ElectricityPriceIncreaseSA2025 = React.lazy(() => import('./components/articles/ElectricityPriceIncreaseSA2025'));
const SolarEWasteSouthAfrica = React.lazy(() => import('./components/articles/SolarEWasteSouthAfrica'));

// Dashboard components - lazy loaded
const EnhancedDashboard = React.lazy(() => import('./components/dashboard/EnhancedDashboard'));
const SystemManagement = React.lazy(() => import('./components/system/SystemManagement'));
const Marketplace = React.lazy(() => import('./components/marketplace/Marketplace'));

// Admin components - lazy loaded
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const ProviderReview = React.lazy(() => import('./components/admin/ProviderReview'));

// Provider components - lazy loaded
const ProviderOnboarding = React.lazy(() => import('./components/provider/ProviderOnboarding'));
const ProviderDashboard = React.lazy(() => import('./components/provider/ProviderDashboard'));

// Other components - lazy loaded
const OfflinePage = React.lazy(() => import('./components/OfflinePage'));
const ProfilePage = React.lazy(() => import('./components/ProfilePage'));
const SettingsPage = React.lazy(() => import('./components/SettingsPage'));

// Keep PWA Install Button as immediate load for better UX
import PWAInstallButton from './components/PWAInstallButton';

// Optimized loading component with favicon
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[200px] py-8">
    <img 
      src="/favicon.svg" 
      alt="Loading..." 
      className="w-12 h-12 animate-spin"
    />
  </div>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/signin" />;
  }
  
  return children;
};

import SolarInstallationCalculatorPage from './pages/calculators/SolarInstallationCalculatorPage';

const CatchAllRedirect = () => {
  return <Navigate to="/" replace />;
};

function App() {
  // Default SEO Configuration
  const defaultSeoData = {
    title: 'Solar Power Solutions | Solar Energy Experts | Solar Insight',
    description: 'Leading provider of solar power solutions in South Africa. Expert installation, maintenance, and support for residential and commercial solar systems. Get reliable solar solutions for load shedding.',
    keywords: 'solar power, solar energy, solar installation, solar panels, solar solutions South Africa, renewable energy, load shedding solutions, solar company, solar experts, solar power systems',
    path: '/',
    image: '/placeholder-image.svg',
    features: [
      'Professional Solar Installation',
      'Load Shedding Solutions',
      'Solar Power Products',
      'Expert Consultation',
      'Maintenance Services'
    ]
  };

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <ThemeProvider>
            <AuthProvider>
              <ScrollToTop />
              <SeoHead template="general" data={defaultSeoData} />
              <PWAInstallButton />
              <Layout>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/load-shedding" element={<LoadSheddingPage />} />
                    
                    {/* Product Routes */}
                    <Route path="/product/all" element={<ProductPage />} />
                    <Route path="/product/panels" element={<SolarPanelPage />} />
                    <Route path="/product/batteries" element={<BatteryPage />} />
                    <Route path="/products/inverters" element={<InverterPage />} />
                    <Route path="/product/mounting" element={<MountingSystemPage />} />
                    <Route path="/product/controllers" element={<ChargeControllerPage />} />
                    <Route path="/product/:category" element={<ProductPage />} />
                    <Route path="/products/:id" element={<ProductDetail />} />

                    {/* Calculator Routes */}
                    <Route path="/calculators/savings" element={<SavingsCalculator />} />
                    <Route path="/calculators/financing" element={<SolarFinancingCalculator />} />
                    <Route path="/calculators/panel" element={<PanelSizeCalculator />} />
                    <Route path="/calculators/battery" element={<BatteryCapacityCalculator />} />
                    <Route path="/calculators/cleaning" element={<CleaningCalculator />} />
                    <Route path="/calculators/geyser" element={<SolarGeyserCalculator />} />
                    <Route path="/calculators/tilt" element={<SolarTiltGauge />} />
                    <Route path="/calculators/installation" element={<SolarInstallationCalculatorPage />} />

                    {/* Other Routes */}
                    <Route path="/insights" element={<InsightPage />} />
                    <Route path="/news" element={<NewsPage />} />
                    <Route path="/news/electricity-price-increase-south-africa-2025" element={<ElectricityPriceIncreaseSA2025 />} />
                    <Route path="/news/load-shedding-south-africa-2025" element={<LoadSheddingSouthAfrica2025 />} />
                    <Route path="/news/solar-registration-laws-2025" element={<SolarRegistrationLaws2025 />} />
                    <Route path="/news/stage-6-loadshedding-south-africa" element={<Stage6LoadsheddingSA />} />
                    <Route path="/news/perovskite-solar-breakthrough-2025" element={<PerovskiteSolarBreakthrough />} />
                    <Route path="/news/solar-e-waste-south-africa" element={<SolarEWasteSouthAfrica />} />
                    <Route path="/reviews" element={<ReviewsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/thank-you" element={<ThankYouPage />} />
                    <Route path="/search" element={<SearchResults />} />
                    <Route path="/signin" element={<SignIn />} />
                    <Route path="/signup" element={<SignUp />} />

                    {/* Service Routes */}
                    <Route path="/services" element={<SolarServicePage />} />
                    <Route path="/services/installation" element={<SolarInstallationPage />} />
                    <Route path="/services/cleaning" element={<SolarCleaningPage />} />
                    <Route path="/services/repair" element={<SolarRepairPage />} />
                    <Route path="/services/maintenance" element={<SolarMaintenancePage />} />

                    {/* Protected Routes */}
                    <Route
                      path="/loadshedding"
                      element={
                        <ProtectedRoute>
                          <LoadSheddingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/battery"
                      element={
                        <ProtectedRoute>
                          <BatteryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/solar-tilt"
                      element={
                        <ProtectedRoute>
                          <SolarTiltGauge />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/savings-calculator"
                      element={
                        <ProtectedRoute>
                          <SavingsCalculator />
                        </ProtectedRoute>
                      }
                    />

                    {/* Social Tips routes */}
                    <Route path="/social-tips" element={<SocialTipsList />} />
                    <Route path="/social-tips/:category/:tipId" element={<SocialTipDetail />} />
                    <Route path="/social-tips/add" element={<AddTip />} />
                    <Route path="/social-tips/manage" element={<ManageTips />} />

                    {/* User Routes */}
                    <Route
                      path="/profile"
                      element={
                        <PrivateRoute>
                          <ProfilePage />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <PrivateRoute>
                          <SettingsPage />
                        </PrivateRoute>
                      }
                    />

                    {/* Enhanced Dashboard Routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <PrivateRoute>
                          <EnhancedDashboard />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/system"
                      element={
                        <PrivateRoute>
                          <SystemManagement />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/system/add"
                      element={
                        <PrivateRoute>
                          <SystemManagement />
                        </PrivateRoute>
                      }
                    />

                    {/* Marketplace Routes */}
                    <Route path="/marketplace" element={<Marketplace />} />

                    {/* Provider Routes */}
                    <Route
                      path="/provider/onboard"
                      element={
                        <PrivateRoute>
                          <ProviderOnboarding />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/provider/dashboard"
                      element={
                        <PrivateRoute>
                          <ProviderDashboard />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/provider/profile"
                      element={
                        <PrivateRoute>
                          <Navigate to="/settings" replace />
                        </PrivateRoute>
                      }
                    />

                    {/* Admin Routes */}
                    <Route
                      path="/admin/dashboard"
                      element={
                        <PrivateRoute>
                          <AdminDashboard />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/admin/providers"
                      element={
                        <PrivateRoute>
                          <ProviderReview />
                        </PrivateRoute>
                      }
                    />

                    {/* PWA Offline Route */}
                    <Route path="/offline" element={<OfflinePage />} />

                    {/* Catch all route */}
                    <Route path="*" element={<CatchAllRedirect />} />
                  </Routes>
                </Suspense>
              </Layout>
            </AuthProvider>
          </ThemeProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;